import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { getContrastColor, COLOR_SUCCESS, COLOR_WARNING, COLOR_INFO, COLOR_SECONDARY, COLOR_DANGER, COLOR_DARK } from '../../shared/colorUtils';
import { useMutation } from '@tanstack/react-query';

const STATUS_COLORS = {
  available: COLOR_SUCCESS,
  rented: COLOR_WARNING,
  in_project: COLOR_INFO,
  maintenance: COLOR_SECONDARY,
  lost: COLOR_DANGER,
  retired: COLOR_DARK,
};

/**
 * QR/barcode camera scanner modal.
 *
 * Props:
 *   contextType  {'project'|'rental'|'none'}  – determines what action buttons appear
 *   contextId    {number}                     – id of the current project or rental
 *   onClose      {function}
 *   onCheckOut   {function(unit)}             – called after successful check-out action
 */
const QRScannerModal = ({ contextType = 'none', contextId, onClose, onCheckOut }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const stoppingScannerRef = useRef(false);
  const [scanResult, setScanResult] = useState(null);   // {type, data}
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const stopScanner = useCallback(async (scannerInstance = scannerRef.current) => {
    if (!scannerInstance || stoppingScannerRef.current) {
      return;
    }

    stoppingScannerRef.current = true;
    try {
      const stopResult = scannerInstance.stop?.();
      if (stopResult && typeof stopResult.then === 'function') {
        await stopResult;
      }
    } catch (err) {
      const message = err?.message || '';
      if (!message.includes('scanner is not running or paused')) {
        throw err;
      }
    } finally {
      if (scannerRef.current === scannerInstance) {
        scannerRef.current = null;
      }
      stoppingScannerRef.current = false;
    }
  }, []);

  const checkOutMutation = useMutation({
    mutationFn: () => {
      const body = contextType === 'rental' ? { rental: contextId } : { project: contextId };
      return api.post(`warehouses/api/item-units/${scanResult.data.id}/check_out/`, body);
    },
    onSuccess: (updated) => {
      setActionMessage(t('warehouses.scanner.checkOutSuccess'));
      if (onCheckOut) onCheckOut(updated);
    },
    onError: (err) => setActionMessage(`${err?.detail || t('warehouses.scanner.checkOutSuccess')}`),
  });

  const checkInMutation = useMutation({
    mutationFn: () => api.post(`warehouses/api/item-units/${scanResult.data.id}/check_in/`, {}),
    onSuccess: (updated) => {
      setActionMessage(t('warehouses.scanner.checkInSuccess'));
      if (onCheckOut) onCheckOut(updated);
    },
    onError: (err) => setActionMessage(`${err?.detail || t('warehouses.scanner.checkInSuccess')}`),
  });

  const actionLoading = checkOutMutation.isPending || checkInMutation.isPending;

  useEffect(() => {
    let scanner = null;

    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            handleScan(decodedText, scanner);
          },
          () => { /* ignore quiet errors */ }
        );
      } catch (err) {
        setError(t('warehouses.scanner.cameraError'));
        setScanning(false);
      }
    };

    startScanner();

    return () => {
      void stopScanner();
    };
  }, [stopScanner]);

  const handleScan = async (code, scanner) => {
    // Stop scanner after first successful scan
    await stopScanner(scanner);
    setScanning(false);

    try {
      const result = await api.get(`warehouses/api/item-units/scan/?code=${encodeURIComponent(code)}`);
      setScanResult(result);
    } catch (err) {
      if (err?.status === 404) {
        setScanResult({ type: 'not_found', code });
      } else {
        setError(t('warehouses.scanner.lookupError'));
      }
    }
  };

  const handleRescan = async () => {
    await stopScanner();
    setScanResult(null);
    setActionMessage(null);
    setError(null);
    setScanning(true);

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => handleScan(decodedText, scanner),
        () => {}
      );
    } catch (err) {
      setError(t('warehouses.scanner.cameraError'));
      setScanning(false);
    }
  };

  const handleCheckOut = () => {
    if (!scanResult?.data?.id) return;
    checkOutMutation.mutate();
  };

  const handleCheckIn = () => {
    if (!scanResult?.data?.id) return;
    checkInMutation.mutate();
  };

  const unit = scanResult?.type === 'unit' ? scanResult.data : null;
  const statusColor = unit ? (STATUS_COLORS[unit.status] || COLOR_SECONDARY) : null;

  return (
    <div className="modal modal-blur fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: 480 }}>
        <div className="modal-content">

          <div className="modal-header">
            <h5 className="modal-title">{t('warehouses.scanner.title')}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {/* Camera view */}
            {scanning && (
              <div id="qr-reader" style={{ width: '100%', minHeight: 280 }} />
            )}

            {!scanning && !scanResult && (
              <div id="qr-reader" style={{ display: 'none' }} />
            )}

            {error && (
              <div className="alert alert-danger">{error}</div>
            )}

            {/* Scan result */}
            {scanResult && (
              <div>
                {scanResult.type === 'not_found' && (
                  <div className="alert alert-warning">
                    {t('warehouses.scanner.notFound', { code: scanResult.code })}
                  </div>
                )}

                {scanResult.type === 'unit' && unit && (
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="mb-0">{unit.product_name}</h5>
                        <span className="badge" style={{ backgroundColor: statusColor, color: getContrastColor(statusColor) }}>{t(`warehouses.units.status_${unit.status}`, unit.status)}</span>
                      </div>
                      <div className="text-muted small mb-1">{t('warehouses.scanner.warehouse')}: {unit.stock_name}</div>
                      {unit.serial_number && <div className="small mb-1">S/N: <strong>{unit.serial_number}</strong></div>}
                      <div className="small mb-1">QR: <code>{unit.qr_code}</code></div>
                      {unit.location_note && <div className="small mb-1">{t('warehouses.units.location')}: {unit.location_note}</div>}
                      {unit.current_rental_ref && <div className="small text-warning">{unit.current_rental_ref}</div>}
                      {unit.current_project_ref && <div className="small text-info">{unit.current_project_ref}</div>}
                    </div>
                  </div>
                )}

                {scanResult.type === 'item' && (
                  <div className="card">
                    <div className="card-body">
                      <h5 className="mb-1">{scanResult.data.name}</h5>
                      <div className="text-muted small">{t('warehouses.scanner.itemTypeLabel')}</div>
                      <div className="small">S/N: <strong>{scanResult.data.serial_number}</strong></div>
                    </div>
                  </div>
                )}

                {actionMessage && (
                  <div className="alert mt-3 alert-success">
                    {actionMessage}
                  </div>
                )}

                {/* Context-aware action buttons */}
                {unit && !actionMessage && (
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    {contextType !== 'none' && unit.status === 'available' && (
                      <button className="btn btn-success" onClick={handleCheckOut} disabled={actionLoading}>
                        {actionLoading ? '…' : contextType === 'rental' ? t('warehouses.scanner.actionCheckOut_rental') : t('warehouses.scanner.actionCheckOut_project')}
                      </button>
                    )}
                    {(unit.status === 'rented' || unit.status === 'in_project') && (
                      <button className="btn btn-warning" onClick={handleCheckIn} disabled={actionLoading}>
                        {actionLoading ? '…' : t('warehouses.scanner.actionCheckIn')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            {scanResult && (
              <button type="button" className="btn btn-outline-secondary" onClick={handleRescan}>
                {t('warehouses.scanner.rescan')}
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('warehouses.scanner.close')}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default QRScannerModal;
